import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import axios, { API, fmt, pctColor, STRATEGY_LABELS } from "./commoditiesLib";
import { CommodityTradeTable } from "./CommodityTradeTable";

const CommoditiesPaperTrading = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("open");
  // 2026-10-27 — chat: "Add Filter in Commodities Paper trading, so that I can select either one of them"
  const [fUnd, setFUnd] = useState("all");
  const [fStrat, setFStrat] = useState("all");
  const [fDir, setFDir] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commodities/paper-trades`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load paper trades");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  // 2026-09-16 — defect found during a review requested after the P&L page
  // Exit fixes (chat: "review all 3 pages... Identify if there are any
  // other defects"). /commodities/paper-trades/manual-exit (this screen's
  // ONLY exit action) is the pre-Kite Upstox-era endpoint — for a
  // live_trade=true row it ONLY closes this app's own paper-book record and
  // explicitly does NOT touch the real broker position at all (see
  // commodity_autopilot.manual_exit's live_note). This screen's handleExit
  // used to call it unconditionally and only ever showed a plain "exited"
  // success toast — an admin could click Exit on a REAL open Kite position
  // here, see success, and reasonably believe the real position was closed,
  // when in fact nothing happened at the broker. Refuse up front instead
  // and point at the Live Orders page, which is now the only screen that
  // places a real Kite SELL (see CommoditiesExecution.jsx).
  const handleExit = async (t) => {
    if (t.live_trade) {
      toast.error(
        `${t.underlying} has a REAL ${t.live_broker || "broker"} position — exit it from the Live Orders page. ` +
        `This screen only closes the paper-book record and does not place a real SELL order.`
      );
      return;
    }
    if (!window.confirm(`Exit ${t.underlying} ${t.option_type} ${t.strike} at the current simulated premium?`)) return;
    try {
      await axios.post(`${API}/commodities/paper-trades/manual-exit`, { trade_id: t.id });
      toast.success(`${t.underlying} exited`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual exit failed");
    }
  };

  const all = useMemo(() => [...(data?.open || []), ...(data?.closed || [])], [data]);
  const unds = useMemo(() => [...new Set(all.map((t) => t.underlying).filter(Boolean))].sort(), [all]);
  const strats = useMemo(() => [...new Set(all.map((t) => t.signal_type).filter(Boolean))].sort(), [all]);
  const keep = useCallback((t) => (fUnd === "all" || t.underlying === fUnd)
    && (fStrat === "all" || t.signal_type === fStrat)
    && (fDir === "all" || t.direction === fDir), [fUnd, fStrat, fDir]);
  const openRows = useMemo(() => (data?.open || []).filter(keep), [data, keep]);
  const closedRows = useMemo(() => (data?.closed || []).filter(keep), [data, keep]);
  const rows = tab === "open" ? openRows : closedRows;
  const filtered = fUnd !== "all" || fStrat !== "all" || fDir !== "all";
  const sum = useMemo(() => {
    const n = closedRows.length;
    const wins = closedRows.filter((t) => Number(t.pnl_pct) > 0).length;
    const net = closedRows.reduce((a, t) => a + (Number(t.net_pnl) || 0), 0);
    const hasNet = closedRows.some((t) => t.net_pnl != null);
    return { n, wins, net, hasNet };
  }, [closedRows]);
  const sel = "bg-transparent border border-grid px-2 py-1 text-[11px] font-mono-data";

  return (
    <section className="space-y-4" data-testid="commodities-paper-trading-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Commodities · MCX
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <Wallet size={20} /> Paper Trading
          </h2>
          {data && (
            <div className="text-[10px] text-muted-3 font-mono-data mt-1">
              {/* 2026-09-22 (chat: "why are we still using Black-Scholes for
                  commodities prices?... fix this") — this used to claim
                  "Simulated (Black-76) pricing throughout" unconditionally,
                  which overstated things: entry/exit pricing already prefers
                  a real MCX quote (Upstox) per trade, falling back to Black-76
                  only when a live quote genuinely isn't resolvable. Each row's
                  own pricing-source line (below its SL/TP) already says which
                  one it actually used — this banner shouldn't contradict it. */}
              Retention: {data.retention_days} days · Paper rows are priced off the live MCX option quote (Kite) —
              Black-76 is a fallback only, see each row's pricing-source line. Rows marked ● carry a REAL Kite fill
              and show real entry/exit/P&amp;L only; exit those from the Live Orders page, not here.
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {["open", "closed"].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`commodities-trades-tab-${k}`}
              className="px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono-data border"
              style={{
                color: tab === k ? "var(--bull)" : "var(--text-muted-2)",
                borderColor: tab === k ? "var(--bull)" : "var(--grid)",
              }}
            >
              {k} {k === "open" ? `(${openRows.length})` : `(${closedRows.length})`}
            </button>
          ))}
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-2" data-testid="commodities-paper-filters">
          <span className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data">Filter</span>
          <select value={fUnd} onChange={(e) => setFUnd(e.target.value)} className={sel} data-testid="commodities-filter-underlying">
            <option value="all">All underlyings</option>
            {unds.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={fStrat} onChange={(e) => setFStrat(e.target.value)} className={sel} data-testid="commodities-filter-strategy">
            <option value="all">All strategies</option>
            {strats.map((s) => <option key={s} value={s}>{STRATEGY_LABELS[s] || s}</option>)}
          </select>
          <select value={fDir} onChange={(e) => setFDir(e.target.value)} className={sel} data-testid="commodities-filter-direction">
            <option value="all">Both directions</option>
            <option value="Bullish">Bullish</option>
            <option value="Bearish">Bearish</option>
          </select>
          {filtered && (
            <button className={`${sel} hover:bg-white/5`} onClick={() => { setFUnd("all"); setFStrat("all"); setFDir("all"); }}>clear</button>
          )}
          <span className="text-[11px] font-mono-data text-muted-2 ml-2" data-testid="commodities-filter-summary">
            Closed: {sum.n} trades · {sum.n ? `${fmt((sum.wins / sum.n) * 100, 0)}% win` : "—"}
            {sum.hasNet ? <> · net <span style={{ color: pctColor(sum.net) }}>₹{fmt(sum.net, 0)}</span></> : null}
          </span>
        </div>
      )}

      {!data ? (
        <div className="text-muted-2 font-mono-data text-xs">Loading…</div>
      ) : (
        <CommodityTradeTable
          trades={rows}
          onExit={handleExit}
          emptyLabel={filtered ? `No ${tab} paper trades match the filter.` : `No ${tab} paper trades.`}
          testid="commodities-paper-trading-table"
        />
      )}
    </section>
  );
};

export default CommoditiesPaperTrading;
